import { Component, OnInit } from '@angular/core';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import {
  ApiExternaCuentaService,
  DesafioLlave,
  PruebaPosesion,
  DocumentacionApi,
  EstadoApiCuenta,
  FilaUsoApi,
  LlaveApi,
} from 'src/app/shared/services/api-externa-cuenta.service';
import { ArchivoDescargado } from 'src/app/shared/utils/descarga-archivo';

type Pestana = 'estado' | 'uso' | 'documentacion' | 'swagger';

/** Qué archivo se baja desde el bloque «Descargas» (fase 3). */
type Descarga = 'openapi' | 'postman' | 'qa' | 'prod' | 'entorno';

/** Paso del asistente de creacion/rotacion. `0` = cerrado. */
type Paso = 0 | 1 | 2 | 3;

const LIMITE_DEFAULT = 30;
const LIMITE_SALDOS_DEFAULT = 10;
const MAX_IPS_DEFAULT = 2;
const VIGENCIA_DEFAULT = 365;
const VERDE = '#2F4A2B';
const ROJO = '#A3432B';
const NOMBRE_OPENAPI = 'aguacate-integraciones-v1.openapi.yaml';
/** Desde cuántos días para vencer la llave se pinta en ámbar. */
const DIAS_AVISO_VENCIMIENTO = 15;

/** Prefijo DER de una clave privada Ed25519 en PKCS#8 (RFC 8410): 16 bytes + 32 de semilla. */
const PKCS8_ED25519_PREFIJO = [
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
];
/** Prefijo DER de una clave publica Ed25519 en SPKI: 12 bytes + 32 de clave. */
const SPKI_ED25519_PREFIJO = [0x30, 0x2a, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x03, 0x21, 0x00];

/** Lo que se muestra UNA sola vez tras crear o rotar. Nunca sale de la memoria del componente. */
interface ClaveRevelada {
  kid: string;
  nombre: string;
  privadaHex: string;
  privadaPem: string;
}

/**
 * Sección «API» del dashboard del cliente (fase 1 + fase 2).
 *
 * Fase 1: estado, desde cuándo y documentación. Al entrar por primera vez con
 * estado INVITADO la cuenta pasa sola a ACTIVO (`activar`).
 *
 * Fase 2: gestión de la llave Ed25519. El par se genera EN EL NAVEGADOR con
 * WebCrypto; al backend viaja sólo la clave pública (hex de 64). La privada
 * se muestra una única vez (hex de 32 bytes + descarga PEM) y después se
 * descarta: no se guarda en `localStorage` ni se vuelve a mostrar. Si el
 * navegador no soporta Ed25519, el cliente pega su clave pública generada con
 * `openssl`.
 */
@Component({
  selector: 'app-api-cliente',
  templateUrl: './api-cliente.component.html',
  styleUrls: ['./api-cliente.component.sass'],
})
export class ApiClienteComponent implements OnInit {
  estado: EstadoApiCuenta | null = null;
  cargando = false;
  error = '';

  pestana: Pestana = 'estado';

  documentacion: DocumentacionApi | null = null;
  cargandoDoc = false;
  errorDoc = '';
  descargandoOpenapi = false;
  errorOpenapi = '';

  // ------------------------------------------------------------ fase 3: swagger y descargas
  /** YAML del OpenAPI ya bajado con el token, para el visor Swagger. `null` = no cargado. */
  openapiYaml: string | null = null;
  cargandoSwagger = false;
  errorSwagger = '';
  /** Descarga en curso del bloque «Descargas» (una a la vez). */
  descargando: Descarga | '' = '';
  errorDescarga = '';

  // ------------------------------------------------------------ llave
  llave: LlaveApi | null = null;
  cargandoLlave = false;
  errorLlave = '';
  avisoLlave = '';
  ipActual: string | null = null;
  /** `maxIps` que trae `GET llaves`; se usa si `GET estado` no lo manda. */
  maxIpsLlaves: number | null = null;
  ocupado = false;

  /**
   * Rotación con gracia: hasta cuándo sigue firmando la llave anterior (ISO).
   * Viene de la respuesta de rotar o de `GET llaves`; se muestra mientras esté en el futuro.
   */
  graciaHasta: string | null = null;
  /** Minutos de gracia elegidos al rotar: 0 = corte inmediato (default). */
  graciaMinutos: 0 | 15 | 60 = 0;
  readonly opcionesGracia: { valor: 0 | 15 | 60; texto: string }[] = [
    { valor: 0, texto: 'No (corte inmediato)' },
    { valor: 15, texto: '15 minutos' },
    { valor: 60, texto: '60 minutos' },
  ];

  /** Asistente de crear/rotar. */
  paso: Paso = 0;
  modo: 'crear' | 'rotar' = 'crear';
  formNombre = '';
  formIps: string[] = ['', ''];
  erroresForm: string[] = [];
  /** `null` = todavía no se probó; `false` = el navegador no genera Ed25519. */
  soportaEd25519: boolean | null = null;
  modoClave: 'navegador' | 'manual' = 'navegador';
  clavePublicaManual = '';

  // ------------------------------------------------------------ prueba de posesión
  /** Desafío vigente del modo «pego la clave pública». */
  desafio: DesafioLlave | null = null;
  cargandoDesafio = false;
  errorDesafio = '';
  firmaManual = '';
  desafioCopiado = false;
  comandoCopiado = false;
  errorCrear = '';
  causasCrear: string[] = [];

  /** Sólo existe entre el alta y «Ya la guardé». */
  revelada: ClaveRevelada | null = null;
  copiado = false;

  /** Edición de IPs de la llave vigente. */
  editandoIps = false;
  formIpsEdicion: string[] = ['', ''];
  erroresIps: string[] = [];
  errorIps = '';

  // ------------------------------------------------------------ uso
  uso: FilaUsoApi[] = [];
  cargandoUso = false;
  errorUso = '';
  usoCargado = false;

  constructor(private api: ApiExternaCuentaService) {}

  ngOnInit(): void {
    void this.cargar();
  }

  async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    try {
      let estado = await this.api.estado();
      // Primera visita de un invitado: queda ACTIVO sin que toque nada.
      if (estado.habilitado && estado.estado === 'INVITADO') {
        try {
          estado = await this.api.activar();
        } catch {
          // Si no se pudo activar, igual se muestra lo que hay: la próxima
          // visita vuelve a intentarlo.
        }
      }
      this.estado = estado;
      if (estado.ipActual) this.ipActual = estado.ipActual;
      if (estado.habilitado) await this.cargarLlave();
    } catch {
      this.estado = null;
      this.error = 'No se pudo consultar el estado de tu acceso a la API. Probá de nuevo en un momento.';
    } finally {
      this.cargando = false;
    }
  }

  // ---------------------------------------------------------------- vista

  get habilitado(): boolean {
    return this.estado?.habilitado === true;
  }

  get deshabilitado(): boolean {
    return this.estado?.estado === 'DESHABILITADO';
  }

  /** Sin fila de cliente API, o con fila pero ya no 100 % activo. */
  get sinAcceso(): boolean {
    return !!this.estado && !this.habilitado && !this.deshabilitado;
  }

  get etiquetaEstado(): string {
    switch (this.estado?.estado) {
      case 'INVITADO':
        return 'Habilitado';
      case 'ACTIVO':
        return 'Activo';
      case 'DESHABILITADO':
        return 'Deshabilitado';
      default:
        return 'Sin acceso';
    }
  }

  get limite(): number {
    return this.estado?.limite?.porMinuto ?? this.estado?.limitePorMinuto ?? LIMITE_DEFAULT;
  }

  get limiteSaldos(): number {
    return this.estado?.limite?.saldosPorMinuto ?? this.estado?.limiteSaldosPorMinuto ?? LIMITE_SALDOS_DEFAULT;
  }

  /** Preferencia: `GET estado` → `GET llaves` → valor del contrato (2). */
  get maxIps(): number {
    return this.estado?.maxIps ?? this.maxIpsLlaves ?? MAX_IPS_DEFAULT;
  }

  get vigenciaDias(): number {
    return this.estado?.vigenciaLlaveDias ?? VIGENCIA_DEFAULT;
  }

  /** Texto del bloque «Descargas» según el entorno que informa `GET estado`. */
  get textoEntorno(): string {
    switch (this.estado?.entorno) {
      case 'prod':
        return 'el entorno trae la URL de producción';
      case 'qa':
        return 'el entorno trae la URL de QA';
      default:
        return 'el entorno trae la URL de la API';
    }
  }

  get todosLosProductos(): boolean {
    return (this.estado?.productos ?? []).includes('*');
  }

  /** `CLP_VES` → «CLP → VES». El cliente no ve el catálogo del panel. */
  get productos(): string[] {
    const lista = this.estado?.productos ?? [];
    return lista
      .filter((id) => id !== '*')
      .map((id) => {
        const partes = String(id).split('_');
        return partes.length === 2 ? `${partes[0]} → ${partes[1]}` : String(id);
      });
  }

  /** Días que faltan para `expiraEn` (negativo si ya venció). */
  get diasParaVencer(): number | null {
    const f = this.llave?.expiraEn;
    if (!f) return null;
    const ms = new Date(f).getTime() - Date.now();
    return Number.isFinite(ms) ? Math.ceil(ms / 86_400_000) : null;
  }

  /** Quedan 15 días o menos (y todavía no venció): se pinta en ámbar. */
  get llavePorVencer(): boolean {
    const d = this.diasParaVencer;
    return d !== null && d > 0 && d <= DIAS_AVISO_VENCIMIENTO;
  }

  /** `graciaHasta` sólo mientras esté en el futuro. */
  get graciaVigente(): string | null {
    const ms = this.graciaHasta ? new Date(this.graciaHasta).getTime() : NaN;
    return Number.isFinite(ms) && ms > Date.now() ? this.graciaHasta : null;
  }

  // ---------------------------------------------------------------- pestañas

  cambiarPestana(p: Pestana): void {
    this.pestana = p;
    if (p === 'documentacion' && !this.documentacion && !this.cargandoDoc) {
      void this.cargarDocumentacion();
    }
    if (p === 'uso' && !this.usoCargado && !this.cargandoUso) {
      void this.cargarUso();
    }
    if (p === 'swagger' && this.openapiYaml === null && !this.cargandoSwagger) {
      void this.cargarSwagger();
    }
  }

  async cargarDocumentacion(): Promise<void> {
    this.cargandoDoc = true;
    this.errorDoc = '';
    try {
      this.documentacion = await this.api.documentacion();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorDoc =
        status === 403
          ? 'La documentación está disponible sólo para cuentas con la API habilitada.'
          : 'No se pudo cargar la documentación. Probá de nuevo en un momento.';
    } finally {
      this.cargandoDoc = false;
    }
  }

  /** Baja el OpenAPI como archivo. 404 = todavía no publicado. */
  async descargarOpenapi(): Promise<void> {
    if (this.descargandoOpenapi) return;
    this.descargandoOpenapi = true;
    this.errorOpenapi = '';
    try {
      const blob = await this.api.openapi();
      saveAs(blob, NOMBRE_OPENAPI);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorOpenapi =
        status === 404
          ? 'El archivo OpenAPI todavía no está disponible.'
          : 'No se pudo descargar el OpenAPI. Probá de nuevo en un momento.';
    } finally {
      this.descargandoOpenapi = false;
    }
  }

  // ------------------------------------------------ fase 3: swagger y descargas

  /** Pestaña «Swagger»: baja el YAML con el token y lo pinta con Swagger UI. */
  async cargarSwagger(): Promise<void> {
    if (this.cargandoSwagger) return;
    this.cargandoSwagger = true;
    this.errorSwagger = '';
    try {
      this.openapiYaml = await this.api.openapiTexto();
    } catch (e) {
      this.openapiYaml = null;
      const status = (e as { status?: number })?.status;
      this.errorSwagger =
        status === 404
          ? 'La especificación OpenAPI todavía no está publicada.'
          : status === 403
          ? 'La especificación está disponible sólo para cuentas con la API habilitada.'
          : 'No se pudo cargar la especificación. Probá de nuevo en un momento.';
    } finally {
      this.cargandoSwagger = false;
    }
  }

  /**
   * Bloque «Descargas»: OpenAPI, colección Postman y entornos. El nombre del
   * archivo lo manda el servidor en `Content-Disposition`.
   */
  async descargar(que: Descarga): Promise<void> {
    if (this.descargando) return;
    this.descargando = que;
    this.errorDescarga = '';
    try {
      const pedidos: Record<Descarga, () => Promise<ArchivoDescargado>> = {
        openapi: () => this.api.openapiArchivo(),
        postman: () => this.api.postman(),
        qa: () => this.api.postmanEntorno('qa'),
        prod: () => this.api.postmanEntorno('prod'),
        entorno: () => this.api.postmanEntornoActual(),
      };
      const archivo = await pedidos[que]();
      saveAs(archivo.blob, archivo.nombre);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorDescarga =
        status === 404
          ? 'Ese archivo todavía no está publicado.'
          : this.mensajeDe(e, 'No se pudo descargar el archivo. Probá de nuevo en un momento.');
    } finally {
      this.descargando = '';
    }
  }

  async cargarUso(): Promise<void> {
    this.cargandoUso = true;
    this.errorUso = '';
    try {
      this.uso = await this.api.uso(7);
      this.usoCargado = true;
    } catch (e) {
      this.errorUso = this.mensajeDe(e, 'No se pudo cargar el uso. Probá de nuevo en un momento.');
    } finally {
      this.cargandoUso = false;
    }
  }

  totalUso(campo: 'ok' | 'http401' | 'http403' | 'http429'): number {
    return this.uso.reduce((s, f) => s + (Number(f[campo]) || 0), 0);
  }

  // ---------------------------------------------------------------- llave

  async cargarLlave(): Promise<void> {
    this.cargandoLlave = true;
    this.errorLlave = '';
    try {
      const r = await this.api.llaves();
      this.llave = r.llave && r.llave.estado === 'ACTIVA' ? r.llave : null;
      if (r.ipActual) this.ipActual = r.ipActual;
      if (typeof r.maxIps === 'number' && r.maxIps > 0) this.maxIpsLlaves = r.maxIps;
      // El backend nuevo informa `llaveEnGracia` siempre (null = gracia cortada o terminada) y manda.
      // Si no lo informa (backend viejo) se conserva la que dejó la rotación.
      if (r.informaGracia) this.graciaHasta = r.graciaHasta ?? null;
      else if (r.graciaHasta) this.graciaHasta = r.graciaHasta;
    } catch (e) {
      this.llave = null;
      this.errorLlave = this.mensajeDe(e, 'No se pudo consultar tu llave. Probá de nuevo en un momento.');
    } finally {
      this.cargandoLlave = false;
    }
  }

  // ------------------------------------------------ asistente: crear / rotar

  abrirCrear(): void {
    this.modo = 'crear';
    this.formNombre = '';
    this.formIps = [this.ipActual ?? '', ''];
    this.erroresForm = [];
    this.errorCrear = '';
    this.causasCrear = [];
    this.clavePublicaManual = '';
    this.modoClave = 'navegador';
    this.paso = 1;
  }

  abrirRotar(): void {
    if (!this.llave) return;
    this.modo = 'rotar';
    this.formNombre = this.llave.nombre;
    this.formIps = [this.llave.ips?.[0] ?? '', this.llave.ips?.[1] ?? ''];
    this.erroresForm = [];
    this.errorCrear = '';
    this.causasCrear = [];
    this.clavePublicaManual = '';
    this.modoClave = 'navegador';
    this.graciaMinutos = 0;
    // Rotar no cambia nombre ni IPs: va directo a la clave.
    this.paso = 2;
  }

  cerrarAsistente(): void {
    if (this.ocupado) return;
    this.paso = 0;
    this.revelada = null;
    this.copiado = false;
  }

  usarIpActual(i: number): void {
    if (this.ipActual) this.formIps[i] = this.ipActual;
  }

  /** Los inputs de IP se bindean por índice: sin trackBy, `*ngFor` los recrea al escribir. */
  trackIndice(i: number): number {
    return i;
  }

  /** Paso 2: al crear vuelve al paso 1; al rotar no hay paso 1, cierra. */
  volverOCancelar(): void {
    if (this.ocupado) return;
    if (this.modo === 'crear') {
      this.paso = 1;
      this.errorCrear = '';
      this.causasCrear = [];
    } else {
      this.cerrarAsistente();
    }
  }

  /** Valida nombre + IPs del paso 1 y avanza al paso 2. */
  continuarAPaso2(): void {
    this.erroresForm = this.validarPaso1();
    if (this.erroresForm.length) return;
    this.paso = 2;
  }

  private validarPaso1(): string[] {
    const errores: string[] = [];
    const nombre = this.formNombre.trim();
    if (nombre.length < 3 || nombre.length > 120) {
      errores.push('El nombre tiene que tener entre 3 y 120 caracteres.');
    }
    errores.push(...validarListaIps(this.formIps, this.maxIps));
    return errores;
  }

  get ipsLimpias(): string[] {
    return this.formIps.map((ip) => ip.trim()).filter(Boolean);
  }

  /**
   * Paso 2: genera el par en el navegador (o toma la pública pegada) y la
   * manda al backend. El resultado va al paso 3, que se muestra una sola vez.
   */
  async generarYEnviar(): Promise<void> {
    if (this.ocupado) return;
    this.errorCrear = '';
    this.causasCrear = [];

    let clavePublicaHex: string;
    let revelada: Omit<ClaveRevelada, 'kid' | 'nombre'> | null = null;
    let prueba: PruebaPosesion | undefined;

    if (this.modoClave === 'navegador') {
      const par = await generarParEd25519();
      if (!par) {
        this.soportaEd25519 = false;
        this.modoClave = 'manual';
        void this.pedirDesafio();
        this.errorCrear =
          'Tu navegador no puede generar claves Ed25519. Generala con openssl y pegá la clave pública acá abajo.';
        return;
      }
      this.soportaEd25519 = true;
      clavePublicaHex = par.publicaHex;
      revelada = { privadaHex: par.privadaHex, privadaPem: par.privadaPem };
      // Prueba de posesión invisible: se pide el desafío y se firma con la privada recién generada.
      this.ocupado = true;
      try {
        const d = await this.api.desafio();
        prueba = { desafio: d.desafio, firma: await firmarTexto(par.privada, d.desafio) };
      } catch (e) {
        this.errorCrear = this.mensajePrueba(e, 'No se pudo preparar la prueba de la clave. Prueba de nuevo en un momento.');
        this.ocupado = false;
        return;
      }
      this.ocupado = false;
    } else {
      const hex = clavePublicaAHex(this.clavePublicaManual);
      if (!hex) {
        this.errorCrear =
          'La clave pública no es válida. Pegá los 64 caracteres hex o el bloque PEM «BEGIN PUBLIC KEY» de una clave Ed25519.';
        return;
      }
      clavePublicaHex = hex;
      if (!this.desafio || this.desafioVencido) {
        this.errorCrear = 'El desafío venció o no está cargado. Pide otro y fírmalo de nuevo.';
        return;
      }
      const firma = this.firmaManual.replace(/\s+/g, '');
      if (!/^[A-Za-z0-9_-]{86}$/.test(firma)) {
        this.errorCrear =
          'La firma no tiene el formato esperado: son 86 caracteres base64url (sin «=»). Copia la salida completa del comando.';
        return;
      }
      prueba = { desafio: this.desafio.desafio, firma };
    }

    this.ocupado = true;
    try {
      const rotando = this.modo === 'rotar' && !!this.llave;
      const nueva =
        rotando && this.llave
          ? await this.api.rotar(this.llave.id, clavePublicaHex, prueba, this.graciaMinutos)
          : await this.api.crearLlave({
              nombre: this.formNombre.trim(),
              ips: this.ipsLimpias,
              clavePublica: clavePublicaHex,
              prueba,
            });
      if (rotando) {
        const g = (nueva as { graciaHasta?: unknown } | null)?.graciaHasta;
        this.graciaHasta = typeof g === 'string' ? g : null;
      }
      this.llave = nueva && nueva.estado !== 'REVOCADA' ? nueva : this.llave;
      this.revelada = revelada
        ? { kid: nueva.kid, nombre: nueva.nombre ?? this.formNombre, ...revelada }
        : null;
      this.copiado = false;
      this.paso = 3;
      this.avisoLlave = '';
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(
        e,
        this.modo === 'rotar' ? 'No se pudo rotar la llave.' : 'No se pudo crear la llave.'
      );
      this.errorCrear = this.mensajePrueba(e, mensaje);
      this.causasCrear = causas;
      // El desafío es de un solo uso: en modo manual se pide otro para el próximo intento.
      if (this.modoClave === 'manual') {
        this.firmaManual = '';
        void this.pedirDesafio();
      }
    } finally {
      this.ocupado = false;
    }
  }

  // ------------------------------------------------ prueba de posesión (modo manual)

  /** Al elegir «pego la clave pública» se pide el desafío si no hay uno vigente. */
  alElegirManual(): void {
    if (!this.desafio || this.desafioVencido) void this.pedirDesafio();
  }

  get desafioVencido(): boolean {
    const ms = this.desafio ? new Date(this.desafio.expiraEn).getTime() : NaN;
    return !Number.isFinite(ms) || ms <= Date.now();
  }

  async pedirDesafio(): Promise<void> {
    if (this.cargandoDesafio) return;
    this.cargandoDesafio = true;
    this.errorDesafio = '';
    this.firmaManual = '';
    try {
      this.desafio = await this.api.desafio();
    } catch (e) {
      this.desafio = null;
      this.errorDesafio = this.mensajePrueba(e, 'No se pudo pedir el desafío. Prueba de nuevo en un momento.');
    } finally {
      this.cargandoDesafio = false;
    }
  }

  /** Comando listo para copiar, con el desafío real (comillas simples escapadas para sh). */
  get comandoFirma(): string {
    const d = (this.desafio?.desafio ?? 'DESAFIO').replace(/'/g, `'\\''`);
    return (
      `printf '%s' '${d}' > desafio.txt\n` +
      `openssl pkeyutl -sign -inkey privada.pem -rawin -in desafio.txt | base64 | tr '+/' '-_' | tr -d '=\\n'`
    );
  }

  async copiarDesafio(): Promise<void> {
    if (!this.desafio) return;
    try {
      await navigator.clipboard.writeText(this.desafio.desafio);
      this.desafioCopiado = true;
      setTimeout(() => (this.desafioCopiado = false), 2500);
    } catch {
      this.desafioCopiado = false;
    }
  }

  async copiarComando(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.comandoFirma);
      this.comandoCopiado = true;
      setTimeout(() => (this.comandoCopiado = false), 2500);
    } catch {
      this.comandoCopiado = false;
    }
  }

  /** Mensajes de la prueba de posesión: `prueba_invalida` (400) y exceso de acciones (429). */
  private mensajePrueba(e: unknown, porDefecto: string): string {
    const status = (e as { status?: number })?.status;
    const codigo = (e as { error?: { codigo?: unknown } })?.error?.codigo;
    if (codigo === 'prueba_invalida') {
      return 'La firma no corresponde a la clave pública o al desafío. Firma el desafío nuevo con la clave privada de esa pública y pega el resultado.';
    }
    if (status === 429) {
      return 'Hiciste demasiadas acciones con tus llaves en la última hora. Espera un rato y vuelve a intentarlo.';
    }
    return this.mensajeDe(e, porDefecto);
  }

  async copiarPrivada(): Promise<void> {
    if (!this.revelada) return;
    try {
      await navigator.clipboard.writeText(this.revelada.privadaHex);
      this.copiado = true;
      setTimeout(() => (this.copiado = false), 2500);
    } catch {
      this.copiado = false;
    }
  }

  descargarPem(): void {
    if (!this.revelada) return;
    const blob = new Blob([this.revelada.privadaPem], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aguacate-${this.revelada.kid}-privada.pem`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /** «Ya la guardé»: descarta la privada para siempre y refresca la tarjeta. */
  async terminarRevelado(): Promise<void> {
    this.revelada = null;
    this.copiado = false;
    this.paso = 0;
    this.avisoLlave =
      this.modo === 'rotar'
        ? this.graciaVigente
          ? 'Llave rotada. Actualiza la clave privada en todos tus servidores antes de que termine la gracia.'
          : 'Llave rotada. La anterior quedó revocada: actualizá la clave privada en tu servidor.'
        : 'Llave creada. Ya podés firmar peticiones con tu clave privada.';
    await this.cargarLlave();
  }

  // ------------------------------------------------ editar IPs

  abrirEditarIps(): void {
    if (!this.llave) return;
    this.formIpsEdicion = [this.llave.ips?.[0] ?? '', this.llave.ips?.[1] ?? ''];
    this.erroresIps = [];
    this.errorIps = '';
    this.editandoIps = true;
  }

  cerrarEditarIps(): void {
    if (this.ocupado) return;
    this.editandoIps = false;
  }

  usarIpActualEnEdicion(i: number): void {
    if (this.ipActual) this.formIpsEdicion[i] = this.ipActual;
  }

  async guardarIps(): Promise<void> {
    if (!this.llave || this.ocupado) return;
    this.erroresIps = validarListaIps(this.formIpsEdicion, this.maxIps);
    if (this.erroresIps.length) return;
    const ips = this.formIpsEdicion.map((ip) => ip.trim()).filter(Boolean);
    this.ocupado = true;
    this.errorIps = '';
    try {
      const actualizada = await this.api.cambiarIps(this.llave.id, ips);
      this.llave = actualizada?.kid ? actualizada : { ...this.llave, ips };
      this.editandoIps = false;
      this.avisoLlave = 'IPs actualizadas. Rigen en menos de un minuto.';
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(e, 'No se pudieron guardar las IPs.');
      this.errorIps = causas.length ? `${mensaje} ${causas.join(' · ')}` : mensaje;
    } finally {
      this.ocupado = false;
    }
  }

  // ------------------------------------------------ revocar

  async revocar(): Promise<void> {
    const l = this.llave;
    if (!l || this.ocupado) return;
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Revocar la llave',
      html:
        `<code>${escapar(l.kid)}</code><br><br>` +
        'Tu sistema deja de poder usar la API en menos de un minuto. ' +
        'Después vas a poder crear una llave nueva.',
      input: 'text',
      inputLabel: 'Motivo (opcional)',
      inputAttributes: { maxlength: '255' },
      showCancelButton: true,
      confirmButtonText: 'Revocar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: ROJO,
    });
    if (!r.isConfirmed) return;
    this.ocupado = true;
    this.errorLlave = '';
    this.avisoLlave = '';
    try {
      await this.api.revocar(l.id, r.value ? String(r.value) : undefined);
      this.llave = null;
      this.avisoLlave = 'Llave revocada. Podés crear una nueva cuando quieras.';
      await this.cargarLlave();
    } catch (e) {
      this.errorLlave = this.mensajeDe(e, 'No se pudo revocar la llave.');
    } finally {
      this.ocupado = false;
    }
  }

  async confirmarRotar(): Promise<void> {
    const l = this.llave;
    if (!l || this.ocupado) return;
    const r = await Swal.fire({
      icon: 'question',
      title: 'Rotar la llave',
      html:
        `<code>${escapar(l.kid)}</code><br><br>` +
        'Se genera un par nuevo y la llave actual queda revocada. ' +
        'En el paso siguiente eliges si se corta en el acto o sigue funcionando unos minutos. ' +
        'Vas a tener que cargar la clave privada nueva en tu servidor.',
      showCancelButton: true,
      confirmButtonText: 'Rotar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!r.isConfirmed) return;
    this.abrirRotar();
  }

  // ---------------------------------------------------------------- internos

  /**
   * El interceptor de errores aplana casi todo a un string; las llamadas que
   * lo saltan traen el `HttpErrorResponse` entero. Se aceptan las dos formas.
   */
  private mensajeDe(e: unknown, porDefecto: string): string {
    if (typeof e === 'string' && e.trim()) return e;
    const cuerpo = (e as { error?: { message?: unknown } })?.error;
    const m = cuerpo?.message;
    if (Array.isArray(m) && m.length) return m.join(' · ');
    if (typeof m === 'string' && m.trim()) return m;
    return porDefecto;
  }

  private errorConCausas(e: unknown, porDefecto: string): { mensaje: string; causas: string[] } {
    const cuerpo = (e as { error?: { causas?: unknown } })?.error;
    const causas = Array.isArray(cuerpo?.causas)
      ? (cuerpo!.causas as unknown[]).map((c) => String(c))
      : [];
    return { mensaje: this.mensajeDe(e, porDefecto), causas };
  }
}

// ==================================================================== helpers

function escapar(s: string): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** IPv4 literal: 4 octetos 0–255 sin ceros a la izquierda raros. */
export function esIpv4(ip: string): boolean {
  const partes = ip.split('.');
  if (partes.length !== 4) return false;
  return partes.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255 && (p === '0' || !p.startsWith('0')));
}

/** IPv6 literal (con o sin `::`, con cola IPv4 opcional). Sin zona ni corchetes. */
export function esIpv6(ip: string): boolean {
  if (!ip || ip.includes('%') || ip.includes('[')) return false;
  const dobles = ip.split('::');
  if (dobles.length > 2) return false;
  const grupo = /^[0-9a-f]{1,4}$/i;
  const contar = (tramo: string): number | null => {
    if (tramo === '') return 0;
    const partes = tramo.split(':');
    let n = 0;
    for (let i = 0; i < partes.length; i++) {
      const p = partes[i];
      if (i === partes.length - 1 && p.includes('.')) {
        if (!esIpv4(p)) return null;
        n += 2;
      } else if (grupo.test(p)) {
        n += 1;
      } else {
        return null;
      }
    }
    return n;
  };
  if (dobles.length === 2) {
    const a = contar(dobles[0]);
    const b = contar(dobles[1]);
    return a !== null && b !== null && a + b <= 7;
  }
  return contar(dobles[0]) === 8;
}

/** No se aceptan privadas ni loopback: el backend lo rechaza igual, pero se avisa antes. */
function esIpPublica(ip: string): boolean {
  if (esIpv4(ip)) {
    const [a, b] = ip.split('.').map(Number);
    if (a === 10 || a === 127 || a === 0) return false;
    if (a === 172 && b >= 16 && b <= 31) return false;
    if (a === 192 && b === 168) return false;
    if (a === 169 && b === 254) return false;
    return true;
  }
  const bajo = ip.toLowerCase();
  if (bajo === '::1' || bajo === '::' || bajo.startsWith('fe80') || /^f[cd]/.test(bajo)) return false;
  return true;
}

function validarListaIps(ips: string[], max: number): string[] {
  const errores: string[] = [];
  const limpias = ips.map((ip) => ip.trim()).filter(Boolean);
  if (!limpias.length) errores.push('Indicá al menos una IP.');
  if (limpias.length > max) errores.push(`Como máximo ${max} IP.`);
  const vistas = new Set<string>();
  for (const ip of limpias) {
    if (!esIpv4(ip) && !esIpv6(ip)) {
      errores.push(`«${ip}» no es una IPv4 ni una IPv6 válida.`);
    } else if (!esIpPublica(ip)) {
      errores.push(`«${ip}» es privada o local: tiene que ser la IP pública de tu servidor.`);
    }
    const clave = ip.toLowerCase();
    if (vistas.has(clave)) errores.push(`«${ip}» está repetida.`);
    vistas.add(clave);
  }
  return errores;
}

function aHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function aBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function deBase64(b64: string): Uint8Array | null {
  try {
    const s = atob(b64.replace(/\s+/g, ''));
    const out = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

function empiezaCon(bytes: Uint8Array, prefijo: number[]): boolean {
  if (bytes.length < prefijo.length) return false;
  return prefijo.every((b, i) => bytes[i] === b);
}

function aPem(der: Uint8Array, etiqueta: string): string {
  const b64 = aBase64(der);
  const lineas = b64.match(/.{1,64}/g) ?? [];
  return `-----BEGIN ${etiqueta}-----\n${lineas.join('\n')}\n-----END ${etiqueta}-----\n`;
}

/**
 * Genera un par Ed25519 con WebCrypto. Devuelve `null` si el navegador no lo
 * soporta (`generateKey` lanza o devuelve algo que no es un par).
 */
async function generarParEd25519(): Promise<{
  publicaHex: string;
  privadaHex: string;
  privadaPem: string;
  /** La privada como CryptoKey, sólo para firmar el desafío; no se guarda. */
  privada: CryptoKey;
} | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const generado = (await subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify'])) as
      | CryptoKeyPair
      | CryptoKey;
    if (!('privateKey' in generado) || !generado.privateKey || !generado.publicKey) return null;
    const publica = new Uint8Array(await subtle.exportKey('raw', generado.publicKey));
    const pkcs8 = new Uint8Array(await subtle.exportKey('pkcs8', generado.privateKey));
    if (publica.length !== 32 || !empiezaCon(pkcs8, PKCS8_ED25519_PREFIJO) || pkcs8.length !== 48) {
      return null;
    }
    return {
      publicaHex: aHex(publica),
      privadaHex: aHex(pkcs8.slice(16)),
      privadaPem: aPem(pkcs8, 'PRIVATE KEY'),
      privada: generado.privateKey,
    };
  } catch {
    return null;
  }
}

/** Firma Ed25519 del texto exacto (UTF-8) y la devuelve en base64url sin `=`. */
async function firmarTexto(privada: CryptoKey, texto: string): Promise<string> {
  const firma = new Uint8Array(
    await globalThis.crypto.subtle.sign({ name: 'Ed25519' }, privada, new TextEncoder().encode(texto))
  );
  return aBase64(firma).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Acepta la clave pública pegada a mano en hex (64) o en PEM/SPKI
 * («BEGIN PUBLIC KEY», salida de `openssl pkey -pubout`) y devuelve hex de 64.
 */
export function clavePublicaAHex(texto: string): string | null {
  const t = (texto ?? '').trim();
  if (!t) return null;
  if (/^[0-9a-f]{64}$/i.test(t)) return t.toLowerCase();
  const cuerpo = t.replace(/-----(BEGIN|END)[^-]*-----/g, '').replace(/\s+/g, '');
  const der = deBase64(cuerpo);
  if (!der) return null;
  if (der.length === 44 && empiezaCon(der, SPKI_ED25519_PREFIJO)) return aHex(der.slice(12));
  if (der.length === 32) return aHex(der);
  return null;
}
